import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { UserAccountLinkComponent } from '../user-account-link/user-account-link.component';

/** One message in the history shown above the reply: something the user sent, or our answer. */
export interface IReplyThreadItem {
  at: string;
  outgoing: boolean;
  label: string;
  subject?: string;
  text: string;
  /** The message being answered now. */
  current?: boolean;
}

/** Greeting and signature around room for the answer, with the user's message quoted below. */
export function buildReplyBody(thanks: string, quoted?: { header: string; text?: string | null }): string {
  const lines = ['Dzień dobry,', '', thanks, '', '', '', 'Pozdrawiamy,', 'Zespół ZdamyPrawko'];
  if (quoted) {
    lines.push('', '---', quoted.header, ...(quoted.text?.split(/\r?\n/).map((line) => `> ${line}`) ?? []));
  }
  return lines.join('\n');
}

/**
 * E-mail answer to a user who wrote to us (problem report, rating, maintenance-screen message):
 * the earlier correspondence, then subject and body. The parent owns the draft and does the sending.
 */
@Component({
  selector: 'app-reply-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, InputTextareaModule,
    UserAccountLinkComponent],
  templateUrl: './reply-dialog.component.html',
  styleUrl: './reply-dialog.component.scss',
})
export class ReplyDialogComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() header = 'Odpowiedź';
  @Input() email?: string | null;
  @Input() userGuid?: string | null;
  @Input() thread: IReplyThreadItem[] = [];
  @Input() subject = '';
  @Output() subjectChange = new EventEmitter<string>();
  @Input() body = '';
  @Output() bodyChange = new EventEmitter<string>();
  /** What sending does besides the e-mail, shown under the message. */
  @Input() hint?: string;
  @Input() sending = false;
  @Input() error: string | null = null;
  @Output() send = new EventEmitter<void>();

  @ViewChild('threadBox') private _threadBox?: ElementRef<HTMLElement>;

  get canSend(): boolean {
    return !this.sending && !!this.subject.trim() && !!this.body.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue && !changes['visible'].previousValue) {
      // The latest messages matter most, so the history opens at its end once the dialog has rendered it
      // ('instant', because the app's global smooth scrolling would animate it).
      setTimeout(() => {
        const thread = this._threadBox?.nativeElement;
        thread?.scrollTo({ top: thread.scrollHeight, behavior: 'instant' });
      });
    }
  }

  close(): void {
    this.visibleChange.emit(false);
  }
}
